import Article, { getArticleName, getArticlePrice } from "../../../global/utils/models/menu/Article";
import { ButtonBase, Typography } from "@mui/material";
import { useDialog } from "../../../global/utils/dialog/DialogProvider";
import SlideUpTransition from "../../../global/components/SlideUpTransition";
import Image from "../../../global/components/Image";
import FormattedMessageJamezz from "../../../global/components/FormattedMessageJamezz";
import useIsWidescreen from "../../../global/utils/useIsWidescreen";
import store, { useAppSelector } from "../../../global/utils/redux/store";
import { selectAppLanguage } from "../../../kiosk/components/LanguageSelector/useLanguage";
import { postAnalyticsEvent, postEcomAnalyticsEvent } from "../../../global/utils/analytics/useAnalytics";
import {
  findInvalidOrderOptionGroup,
  initOrderArticle,
  OrderArticleAnalytics,
} from "../../../global/utils/models/order/OrderArticle.ts";
import OrderArticleSelectorPage from "../OrderArticleSelectorPage/OrderArticleSelectorPage.tsx";
import { selectArticlesMap } from "../../../global/utils/redux/selectors/selectArticlesMap.ts";
import useIsOrderingAllowed from "../../../global/utils/hooks/useIsOrderingAllowed.ts";
import useAddOrderArticleToShoppingCart from "../../../global/utils/order/useAddOrderArticleToShoppingCart.tsx";
import { makeGridArea } from "./ArticleSuggestionPage.tsx";
import Box from "@mui/material/Box";
import { selectSalesAreaPriceLineId } from "../../../global/utils/useSalesAreaPriceLineId.ts";
import FormatCurrency from "../../../global/components/Currencies/FormatCurrency.tsx";

interface Props {
  upsellDetails?: Pick<OrderArticleAnalytics, "upsellType" | "upsellSource" | "upsellId">;
  article: Article;
  upsell_text: string;
  upsell_title: string;
  useAsDialog: boolean;
  columnIndex: number;
}

export default function ArticleSuggestionItem(props: Props) {
  const { openDialog, closeDialog } = useDialog();
  const lang = useAppSelector(selectAppLanguage);
  const isWidescreen = useIsWidescreen();
  const canOrder = useIsOrderingAllowed(undefined, props.article);
  const addOrderArticleToShoppingCart = useAddOrderArticleToShoppingCart({ allowUpsell: false });

  const priceLineId = useAppSelector(selectSalesAreaPriceLineId);
  const price = getArticlePrice(props.article, { salesAreaPriceLineId: priceLineId });
  const submitEComPromotion = () => {
    postAnalyticsEvent({
      category: "ArticleSuggestionItem",
      action: "OrderArticleSelectorPage",
      label: props.article.name,
    });
    postEcomAnalyticsEvent("select_promotion", {
      creative_name: props.upsell_title ?? "n/a",
      creative_slot: props.upsell_title ?? "n/a",
      promotion_id: props.upsellDetails?.upsellId ?? "UPSELL_" + props.article.id,
      promotion_name: props.upsell_text ?? "n/a",
      items: [
        {
          item_id: props.article.id,
          item_name: props.article.name,
          price: price,
          quantity: 1,
        },
      ],
    });
  };

  const articleIsNotAvailable = props.article.isNotAvailable;
  return (
    <>
      <ButtonBase
        className={"JS-ArticleSuggestionPage-Root"}
        sx={{
          width: 1,
          borderRadius: 3,
          boxShadow: "1px 2px 2px 0px rgba(0,0,0,0.1)",
          gridArea: makeGridArea(props.columnIndex, 0, 4),
        }}
        disabled={articleIsNotAvailable}
        onClick={(e) => {
          e.stopPropagation();
          const orderArticle = initOrderArticle(selectArticlesMap(store.getState()), props.article, 1, {
            isUpselled: true,
            ...props.upsellDetails,
          });
          if (
            !findInvalidOrderOptionGroup(orderArticle) &&
            canOrder == "ALLOWED" &&
            orderArticle.orderOptionGroups.length === 0
          ) {
            addOrderArticleToShoppingCart(orderArticle).catch((err) => console.log(err));
            closeDialog();
          } else {
            openDialog({
              children: (
                <OrderArticleSelectorPage
                  article={props.article}
                  isUpselled={true}
                  upsellDetails={props.upsellDetails}
                />
              ),
              fullScreen: !isWidescreen,
              TransitionComponent: SlideUpTransition,
            });

            submitEComPromotion();
          }
        }}
      ></ButtonBase>

      {props.article.imageMediaUrls?.[0]?.conversions?.responsive?.srcset ? (
        <Image
          srcSet={props.article.imageMediaUrls?.[0]?.conversions?.responsive?.srcset}
          style={{ width: "100%", maxHeight: 236, objectFit: "contain", gridArea: makeGridArea(props.columnIndex, 0) }}
        />
      ) : null}
      <Typography sx={{ textAlign: "center", fontWeight: 800, gridArea: makeGridArea(props.columnIndex, 1) }}>
        {getArticleName(props.article, lang)}
      </Typography>
      {price > 0 || props.article.vaprice > 0 ? (
        <Typography
          sx={{ textAlign: "center", fontWeight: 800, mb: 1, gridArea: makeGridArea(props.columnIndex, 2) }}
          className={"JS-ArticleSuggestionItem-Price"}
        >
          <FormatCurrency value={props.article.vaprice > 0 ? props.article.vaprice : price} />
        </Typography>
      ) : null}
      {articleIsNotAvailable ? (
        <Box
          sx={{
            gridArea: makeGridArea(props.columnIndex, 0, 4),
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 4,
          }}
        >
          {props.article.isOutOfStock ? (
            <Typography
              variant={"h6"}
              sx={{ fontWeight: 1000, textAlign: "center" }}
              className={"JS-ArticleSuggestionItem-OutOfStock-Text"}
            >
              <FormattedMessageJamezz id={"OUT OF STOCK"} />
            </Typography>
          ) : (
            <Typography
              variant={"h6"}
              sx={{ fontWeight: 1000, textAlign: "center" }}
              className={"JS-ArticleSuggestionItem-NotAvailable-Text"}
            >
              <FormattedMessageJamezz id={"NOT AVAILABLE"} />
            </Typography>
          )}
        </Box>
      ) : null}
    </>
  );
}
